import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Spinner } from '../../components/Spinner';
import { useToast } from '../../lib/toast';
import { formatRelative } from '../../lib/format';
import { ROLES } from '@shared/constants';
import type { Organization, User } from '@shared/types';

interface ListResult {
  items: User[];
  total: number;
}

export function AdminUsersPage() {
  const [data, setData] = useState<ListResult | null>(null);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    email: '',
    name: '',
    role: 'member' as (typeof ROLES)[number],
    organizationId: '',
    password: '',
  });
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  async function load() {
    try {
      const [users, orgList] = await Promise.all([
        api.get<ListResult>('/api/admin/users?pageSize=200'),
        api.get<{ items: Organization[] }>('/api/admin/orgs?pageSize=200'),
      ]);
      setData(users);
      setOrgs(orgList.items);
      if (!form.organizationId && orgList.items[0]) {
        setForm((f) => ({ ...f, organizationId: orgList.items[0].id }));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/api/admin/users', form);
      toast.success('User created');
      setShowCreate(false);
      setForm({
        email: '',
        name: '',
        role: 'member',
        organizationId: orgs[0]?.id ?? '',
        password: '',
      });
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Users</h1>
          <p className="text-sm text-ink-500 dark:text-ink-400">
            Manage every user across all organizations.
          </p>
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={() => setShowCreate((v) => !v)}
        >
          {showCreate ? 'Cancel' : 'New user'}
        </button>
      </header>

      {showCreate && (
        <form onSubmit={onCreate} className="card p-5 grid gap-3 md:grid-cols-2">
          <div>
            <label className="label">Email</label>
            <input
              required
              type="email"
              className="input"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
          </div>
          <div>
            <label className="label">Name</label>
            <input
              className="input"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>
          <div>
            <label className="label">Role</label>
            <select
              className="input"
              value={form.role}
              onChange={(e) =>
                setForm((f) => ({ ...f, role: e.target.value as (typeof ROLES)[number] }))
              }
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Organization</label>
            <select
              className="input"
              value={form.organizationId}
              onChange={(e) =>
                setForm((f) => ({ ...f, organizationId: e.target.value }))
              }
            >
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="label">Initial password</label>
            <input
              type="password"
              className="input"
              autoComplete="new-password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving && <Spinner size={16} />} Create user
            </button>
          </div>
        </form>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-200 px-4 py-3 text-sm">
          {error}
        </div>
      )}
      {!data && (
        <div className="flex items-center justify-center py-16">
          <Spinner size={28} />
        </div>
      )}
      {data && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last login</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((u) => (
                <tr key={u.id}>
                  <td>{u.email}</td>
                  <td>{u.name ?? '—'}</td>
                  <td>
                    <span className="badge-info">{u.role}</span>
                  </td>
                  <td>
                    <span
                      className={u.status === 'active' ? 'badge-success' : 'badge-muted'}
                    >
                      {u.status}
                    </span>
                  </td>
                  <td className="text-xs text-ink-500 dark:text-ink-400">
                    {formatRelative(u.lastLoginAt)}
                  </td>
                  <td className="text-right">
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={async () => {
                        const newRole = prompt(`New role for ${u.email}`, u.role);
                        if (!newRole || newRole === u.role) return;
                        try {
                          await api.patch(`/api/admin/users/${u.id}`, { role: newRole });
                          toast.success('Updated');
                          await load();
                        } catch (err) {
                          toast.error(err instanceof Error ? err.message : 'Failed');
                        }
                      }}
                    >
                      Change role
                    </button>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={async () => {
                        const next = u.status === 'active' ? 'disabled' : 'active';
                        try {
                          await api.patch(`/api/admin/users/${u.id}`, { status: next });
                          toast.success(`User ${next}`);
                          await load();
                        } catch (err) {
                          toast.error(err instanceof Error ? err.message : 'Failed');
                        }
                      }}
                    >
                      {u.status === 'active' ? 'Disable' : 'Enable'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
